import React, { useState, useEffect, useCallback } from 'react';
import { Cake, Gift, Send, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { TextArea } from './TextArea';
import { Badge } from './ui/badge';
import { Skeleton } from './Skeleton';
import { Aniversariante } from '../types/aniversariantes';
import {
  listAniversariantesMes,
  enviarMensagemAniversario,
} from '../services/aniversariantesService';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

function getAge(dataNascimento: string): number {
  const hoje = new Date();
  const nascimento = new Date(dataNascimento + 'T00:00:00');
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--;
  }
  return idade;
}

function getDiaAniversario(dataNascimento: string): number {
  return parseInt(dataNascimento.split('-')[2], 10);
}

function isThisWeek(dataNascimento: string): boolean {
  const hoje = new Date();
  const [, mesNasc, diaNasc] = dataNascimento.split('-').map(Number);

  // Só considera se é do mês atual
  if (mesNasc !== hoje.getMonth() + 1) return false;

  // Calcula início e fim da semana (domingo a sábado)
  const diaSemana = hoje.getDay();
  const inicioSemana = new Date(hoje);
  inicioSemana.setDate(hoje.getDate() - diaSemana);
  inicioSemana.setHours(0, 0, 0, 0);

  const fimSemana = new Date(inicioSemana);
  fimSemana.setDate(inicioSemana.getDate() + 6);
  fimSemana.setHours(23, 59, 59, 999);

  // Cria data do aniversário no ano atual
  const dataAniversario = new Date(hoje.getFullYear(), mesNasc - 1, diaNasc);

  return dataAniversario >= inicioSemana && dataAniversario <= fimSemana;
}

interface EnviarMensagemDialogProps {
  aniversariante: Aniversariante;
  onClose: () => void;
  onSuccess: () => void;
}

function EnviarMensagemDialog({ aniversariante, onClose, onSuccess }: EnviarMensagemDialogProps) {
  const [mensagem, setMensagem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mensagem.trim()) return;

    setIsSubmitting(true);
    try {
      await enviarMensagemAniversario(aniversariante.id, mensagem.trim());
      toast({ title: 'Sucesso!', description: `Mensagem enviada para ${aniversariante.nick}!` });
      onSuccess();
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Erro ao enviar mensagem';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-pink-500" />
            Felicitar {aniversariante.nick}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              Escreva uma mensagem de aniversário para{' '}
              <strong>{aniversariante.nick}</strong> ({aniversariante.patente.patente}).
            </p>
            <TextArea
              title="Mensagem"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder="Escreva sua mensagem de felicitações..."
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right mt-1">{mensagem.length}/500</p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!mensagem.trim() || isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AniversariantesSemana() {
  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [felicitados, setFelicitados] = useState<Set<string>>(new Set());
  const [selectedAniversariante, setSelectedAniversariante] = useState<Aniversariante | null>(null);
  const { militar } = useAuth();
  const { toast } = useToast();

  const anoAtual = new Date().getFullYear();

  const loadAniversariantes = useCallback(async () => {
    setIsLoading(true);
    try {
      const mesAtual = new Date().getMonth() + 1;
      const data = await listAniversariantesMes(mesAtual);
      // Filtrar apenas os da semana
      const daSemana = data.filter(a => isThisWeek(a.data_nascimento));
      setAniversariantes(daSemana);
    } catch {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar aniversariantes',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAniversariantes();
  }, [loadAniversariantes]);

  // Carregar quais já foram felicitados (persistido no localStorage por ano)
  useEffect(() => {
    if (!militar?.id) return;
    const key = `aniversario_felicitados_${anoAtual}_${militar.id}`;
    const salvo = localStorage.getItem(key);
    if (salvo) {
      try {
        setFelicitados(new Set(JSON.parse(salvo)));
      } catch {
        // ignora
      }
    }
  }, [militar?.id, anoAtual]);

  const marcarFelicitado = (id: string) => {
    setFelicitados((prev) => {
      const novo = new Set(prev);
      novo.add(id);
      if (militar?.id) {
        const key = `aniversario_felicitados_${anoAtual}_${militar.id}`;
        localStorage.setItem(key, JSON.stringify(Array.from(novo)));
      }
      return novo;
    });
  };

  const handleFelicitar = (aniversariante: Aniversariante) => {
    setSelectedAniversariante(aniversariante);
  };

  const handleMensagemSucesso = () => {
    if (selectedAniversariante) {
      marcarFelicitado(selectedAniversariante.id);
    }
    setSelectedAniversariante(null);
  };

  if (isLoading) {
    return (
      <div className="relative">
        <button
          className="group relative flex items-center justify-center w-10 h-10 rounded-full bg-pink-500/10 hover:bg-pink-500/20 transition-colors cursor-wait"
          disabled
        >
          <Cake className="w-5 h-5 text-pink-500 animate-pulse" />
        </button>
      </div>
    );
  }

  if (aniversariantes.length === 0) {
    return null;
  }

  return (
    <>
      {/* Botão Flutuante com Badge */}
      <div className="relative">
        <button
          onClick={() => setShowModal(true)}
          className="group relative flex items-center justify-center w-10 h-10 rounded-full bg-pink-500/10 hover:bg-pink-500/20 transition-all hover:scale-110"
          title={`${aniversariantes.length} aniversariante${aniversariantes.length > 1 ? 's' : ''} esta semana`}
        >
          <Cake className="w-5 h-5 text-pink-500" />
          {aniversariantes.length > 0 && (
            <Badge
              className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center text-[10px] bg-pink-600 hover:bg-pink-600"
            >
              {aniversariantes.length}
            </Badge>
          )}
        </button>
      </div>

      {/* Modal com Lista de Aniversariantes */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cake className="w-5 h-5 text-pink-500" />
              Aniversariantes da Semana
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {aniversariantes.map((aniversariante) => {
              const jaFelicitou = felicitados.has(aniversariante.id);
              const dia = getDiaAniversario(aniversariante.data_nascimento);
              const idade = getAge(aniversariante.data_nascimento) + 1;

              return (
                <Card key={aniversariante.id} className="transition-all hover:border-pink-400/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="relative flex-shrink-0">
                        <img
                          src={`https://www.habbo.com.br/habbo-imaging/avatarimage?img_format=png&user=${aniversariante.nick}&direction=3&head_direction=2&size=m&headonly=1`}
                          alt={aniversariante.nick}
                          className="w-14 h-14 rounded-full object-cover bg-muted"
                          loading="lazy"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-base">{aniversariante.nick}</p>
                          <span className="text-lg" title="Aniversário">🎂</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{aniversariante.patente.patente}</p>
                        <p className="text-sm text-muted-foreground">
                          {dia} de {new Date(2000, new Date().getMonth(), 1).toLocaleString('pt-BR', { month: 'long' })} · {idade} anos
                        </p>
                      </div>

                      <Button
                        size="sm"
                        variant={jaFelicitou ? 'outline' : 'default'}
                        className={`${jaFelicitou ? 'opacity-60' : ''}`}
                        onClick={() => !jaFelicitou && handleFelicitar(aniversariante)}
                        disabled={jaFelicitou}
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        {jaFelicitou ? 'Enviado' : 'Felicitar'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Enviar Mensagem */}
      {selectedAniversariante && (
        <EnviarMensagemDialog
          aniversariante={selectedAniversariante}
          onClose={() => setSelectedAniversariante(null)}
          onSuccess={handleMensagemSucesso}
        />
      )}
    </>
  );
}
